import {
	DeviceAuthentication,
	DeviceBatteryLevel,
	DeviceCompatibilityLevel,
	DeviceLicense,
	DeviceLicenseWarningDetails,
	DeviceMicState,
	DeviceModel,
	DeviceStatus
} from '@shure/shared/angular/data-access/system-api/models';
import { FirmwarePackage } from '@shure/shared/models';

import { CloudGateway } from './cloud-gateway';
import { FirmwareUpdateProgress, FirmwareUpdateStage } from './firmware-update-progress';
import { LicenseV3 } from './license-v3';
import { MissingFeatureType } from './missing-feature-type';
import { NetworkInterface } from './network-interface';
import { ProxiedDevice } from './proxied-device';
import { SortedDeviceConditionCollection } from './sorted-device-condition-collection';

export interface InventoryDevice {
	id: string;
	name: string;
	model: DeviceModel;
	virtual: boolean;
	status: DeviceStatus;
	hardwareId?: string;
	compatibility: DeviceCompatibilityLevel;
	manufacturer?: string;
	features: {
		audioChannelCount?: {
			count: number;
		} & MissingFeatureType;
		audioNetwork?: NetworkInterface & MissingFeatureType;
		authentication?: DeviceAuthentication & MissingFeatureType;
		availablePackages?: {
			primaryPackages: FirmwarePackage[];
			hostedPackages: FirmwarePackage[];
		} & MissingFeatureType;
		batteryLevel?: DeviceBatteryLevel & MissingFeatureType;
		cloudGateway?: CloudGateway & MissingFeatureType;
		controlNetwork?: NetworkInterface & MissingFeatureType;
		danteName?: {
			danteName: string;
		} & MissingFeatureType;
		firmware?: {
			version: string;
			valid: boolean;
			isUpToDate: boolean;
		} & MissingFeatureType;
		hostedFirmware?: FirmwarePackage & MissingFeatureType;
		hostedUpdateProgress?: FirmwareUpdateProgress & MissingFeatureType;
		identify?: {
			identifying: boolean;
		} & MissingFeatureType;
		license?: DeviceLicense<DeviceLicenseWarningDetails> & MissingFeatureType;
		licenseV3?: {
			license?: LicenseV3;
		} & MissingFeatureType;
		micStatus?: {
			status: DeviceMicState;
		} & MissingFeatureType;
		mute?: {
			muted: boolean;
		} & MissingFeatureType;
		proxiedDevices?: {
			proxiedDevices: ProxiedDevice[];
		} & MissingFeatureType;
		serialNumber?: {
			serialNumber: string;
		} & MissingFeatureType;
		updateProgress?: FirmwareUpdateProgress & MissingFeatureType;
		uptime?: {
			uptime: string;
		} & MissingFeatureType;
		room?: {
			room: {
				name: string;
			};
		} & MissingFeatureType;
		tags?: {
			tags: string[];
		} & MissingFeatureType;
	};
	conditions: SortedDeviceConditionCollection;
}

export function isDeviceUpdating(device: InventoryDevice): boolean {
	return isUpdateInProgress(device.features.updateProgress?.stage);
}

export function isUpdateInProgress(stage?: FirmwareUpdateStage): boolean {
	return (
		stage === FirmwareUpdateStage.Pending ||
		stage === FirmwareUpdateStage.DeviceReserved ||
		stage === FirmwareUpdateStage.TransferringFiles ||
		stage === FirmwareUpdateStage.PreInstallValidating ||
		stage === FirmwareUpdateStage.Installing ||
		stage === FirmwareUpdateStage.Rebooting ||
		stage === FirmwareUpdateStage.Verifying
	);
}

export function isUpdateActive(stage: FirmwareUpdateStage): boolean {
	return isUpdateInProgress(stage) && !isUpdatePending(stage);
}

export function isUpdatePending(stage?: FirmwareUpdateStage): boolean {
	return stage === FirmwareUpdateStage.Pending;
}

export function isUpdateEnded(stage?: FirmwareUpdateStage): boolean {
	return (
		stage === FirmwareUpdateStage.Complete ||
		stage === FirmwareUpdateStage.Canceled ||
		stage === FirmwareUpdateStage.Error
	);
}
